import React, { useState, useEffect } from 'react';
import ManagerSidebar from '../../components/ManagerSidebar';
import NoticeComponent from '../../components/NoticeComponent';
import { instance } from '../../api/axiosInstance';
import { UserDTO } from '../profileSetting/UserDTO';

export default function ManagerAdmin() {
  const [users, setUsers] = useState<UserDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await instance.get<UserDTO[]>('/api/users');
        setUsers(response.data);
      } catch (err: any) {
        setError(err.message || '사용자 데이터를 불러오는 데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // 정렬 함수
  const handleSort = (key: keyof UserDTO) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    setUsers((prevUsers) =>
      [...prevUsers].sort((a, b) => {
        if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
        if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
        return 0;
      })
    );
  };

  const handleDelete = async (userId: number) => {
    if (!window.confirm('정말 이 사용자를 삭제하시겠습니까?')) return;

    try {
      await instance.delete(`/api/users/${userId}`);
      setUsers((prevUsers) => prevUsers.filter((user) => user.id !== userId));
    } catch (err: any) {
      alert(err.response?.data?.message || '삭제 중 오류가 발생했습니다.');
    }
  };

  if (loading) {
    return (
      <section className="flex items-center justify-center h-screen">
        <p>로딩 중...</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="flex items-center justify-center h-screen">
        <p className="text-red-500">{error}</p>
      </section>
    );
  }

  // 정렬 상태에 따라 아이콘 표시
  const getSortIcon = (key: string) => {
    if (sortConfig?.key === key) {
      return sortConfig.direction === 'asc' ? '🔼' : '🔽';
    }
    return '🔽'; // 기본 아이콘
  };

  return (
    <section className="max-w-[2560px] w-full mx-auto h-screen flex mt-[90px]">
      <ManagerSidebar />

      <article className="max-w-[1440px] w-full mx-auto flex-1 py-8">
        <NoticeComponent title="고객관리" content="" />

        <div className="border border-[#f6f6f6] shadow-custom bg-white rounded-lg mt-[28px]">
          <table className="min-w-full border-collapse table-fixed relative">
            <thead>
              <tr className="tracking-wider h-[50px] Pretendard-Regular flex items-center bg-slate-50 text-[#545454] text-sm border-b border-b-[#f0f0f0]">
                <th className="w-[127px] px-4 py-2 text-left">프로필</th>
                <th
                  className="w-[256px] px-4 py-2 text-left cursor-pointer flex items-center"
                  onClick={() => handleSort('nickname')}
                >
                  크리에이터 닉네임{' '}
                  <span className="ml-1 mt-[2px] text-[10px]">
                    {getSortIcon('nickname')}
                  </span>
                </th>
                <th
                  className="w-[250px] px-4 py-2 text-left cursor-pointer flex items-center"
                  onClick={() => handleSort('phone')}
                >
                  번호{' '}
                  <span className="ml-1 mt-[2px] text-[10px]">
                    {getSortIcon('phone')}
                  </span>
                </th>
                <th
                  className="w-[395px] px-4 py-2 text-left cursor-pointer flex items-center"
                  onClick={() => handleSort('email')}
                >
                  이메일{' '}
                  <span className="ml-1 mt-[2px] text-[10px]">
                    {getSortIcon('email')}
                  </span>
                </th>
                <th
                  className="px-4 py-2 text-left cursor-pointer flex items-center"
                  onClick={() => handleSort('roles')}
                >
                  등급{' '}
                  <span className="ml-1 mt-[2px] text-[10px]">
                    {getSortIcon('roles')}
                  </span>
                </th>
                <th className="px-4 py-2 text-center"></th>
                <th className="px-4 py-2 text-center"></th>
              </tr>
            </thead>
          </table>

          {/* 스크롤 가능한 영역 */}
          <div className="scrollable-div overflow-y-auto h-[650px] Pretendard-Medium">
            <table className="min-w-full border-collapse table-fixed">
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="text-MainTextColor">
                    <td className="px-4 py-4 text-left">
                      <img
                        src={`${instance.defaults.baseURL}${user.profileImageUrl}`}
                        alt={user.username}
                        className="w-10 h-10 rounded-lg"
                      />
                    </td>
                    <td className="px-4 py-4 text-left">{user.nickname}</td>
                    <td className="px-4 py-4 text-left">{user.phone}</td>
                    <td className="px-4 py-4 text-left">{user.email}</td>
                    <td className="px-4 py-4 text-left">{user.roles}</td>

                    <td className="px-4 py-4 text-center">
                      <div className="flex justify-center">
                        <button
                          className="text-red-500"
                          onClick={() => handleDelete(user.id)}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex justify-center">
                        <button
                          className={`transition duration-300 ease-in-out Pretendard-Medium tracking-wide px-4 py-1 rounded ${
                            user._seller
                              ? 'text-[#ff4040]'
                              : 'text-[#3c76f1]'
                          }`}
                        >
                          {user._seller ? '비활성화' : '활성화'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </article>
    </section>
  );
}
